import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ClinicDialog } from './ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import promptService, { Prompt, PromptProvider, PromptThinkingLevel, MODEL_OPTIONS } from '../../services/promptService';
import { getApiError } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  meetingId: number;
  meetingTypeId?: number | null;
  zIndex?: number;
}

const menuProps = (baseZIndex?: number) =>
  baseZIndex !== undefined ? { sx: { zIndex: baseZIndex + 100 } } : undefined;

export const RunMeetingSummaryDialog: React.FC<Props> = ({ open, onClose, meetingId, meetingTypeId, zIndex }) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
  const [action, setAction] = useState<'update_summary' | 'send_summary'>('send_summary');
  const [provider, setProvider] = useState<PromptProvider | ''>('');
  const [model, setModel] = useState<string>('');
  const [thinkingLevel, setThinkingLevel] = useState<PromptThinkingLevel | ''>('');
  const [loading, setLoading] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQueued(false);
    setError(null);
    setSelectedPromptId('');
    setProvider('');
    setModel('');
    setThinkingLevel('');

    promptService
      .getPrompts('meeting_summary', true)
      .then((all) => {
        const filtered = meetingTypeId
          ? all.filter((p) => p.meeting_type_id === meetingTypeId)
          : all;
        setPrompts(filtered);
        if (filtered.length > 0) {
          const first = filtered[0];
          setSelectedPromptId(first.id);
          setProvider((first.model_provider as PromptProvider) || '');
          setModel(first.model || '');
          setThinkingLevel((first.thinking_level as PromptThinkingLevel) || '');
        }
      })
      .catch(() => setPrompts([]));
  }, [open, meetingTypeId]);

  const handlePromptChange = (promptId: number | '') => {
    setSelectedPromptId(promptId);
    const p = prompts.find((x) => x.id === promptId);
    if (p) {
      setProvider((p.model_provider as PromptProvider) || '');
      setModel(p.model || '');
      setThinkingLevel((p.thinking_level as PromptThinkingLevel) || '');
    }
  };

  const handleProviderChange = (val: PromptProvider | '') => {
    setProvider(val);
    setModel('');
  };

  const handleRun = async () => {
    if (!selectedPromptId) {
      setError('Please select a prompt.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await promptService.runMeetingSummary(
        meetingId,
        selectedPromptId as number,
        action,
        {
          model_provider: provider || null,
          model: model || null,
          thinking_level: thinkingLevel || null,
        },
      );
      setQueued(true);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to queue job'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQueued(false);
    setError(null);
    onClose();
  };

  return (
    <ClinicDialog
      open={open}
      onClose={handleClose}
      title="Run Meeting Summary"
      maxWidth="sm"
      zIndex={zIndex}
      actions={
        <>
          {!queued && (
            <Button
              onClick={handleRun}
              variant="contained"
              disabled={loading || !selectedPromptId}
              sx={CLINIC_DIALOG_SX.primaryButton}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {loading ? 'Queuing...' : 'Run'}
            </Button>
          )}
          <Button onClick={handleClose} sx={CLINIC_DIALOG_SX.cancelButton}>
            {queued ? 'Close' : 'Cancel'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {queued ? (
          <Alert severity="success">
            {action === 'update_summary'
              ? 'Job queued — the summary will be saved to the meeting once complete.'
              : 'Job queued — the summary will be sent to your email once complete.'}
          </Alert>
        ) : (
          <>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Prompt</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedPromptId}
                  onChange={(e) => handlePromptChange(e.target.value as number | '')}
                  displayEmpty
                  style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                  MenuProps={menuProps(zIndex)}
                >
                  <MenuItem value="" disabled><em>Select prompt...</em></MenuItem>
                  {prompts.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Action</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={action}
                  onChange={(e) => setAction(e.target.value as 'update_summary' | 'send_summary')}
                  style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                  MenuProps={menuProps(zIndex)}
                >
                  <MenuItem value="update_summary">Update Meeting Summary</MenuItem>
                  <MenuItem value="send_summary">Send Summary by Email</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Provider</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={provider}
                    onChange={(e) => handleProviderChange(e.target.value as PromptProvider | '')}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                    MenuProps={menuProps(zIndex)}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="claude">Claude</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 2, minWidth: 120 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Model</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                    MenuProps={menuProps(zIndex)}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    {(MODEL_OPTIONS[provider] || []).map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Thinking</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={thinkingLevel}
                    onChange={(e) => setThinkingLevel(e.target.value as PromptThinkingLevel | '')}
                    displayEmpty
                    style={CLINIC_DIALOG_SX.textField as React.CSSProperties}
                    MenuProps={menuProps(zIndex)}
                  >
                    <MenuItem value=""><em>Default</em></MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {prompts.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No active prompts found for this meeting type.
              </Typography>
            )}
          </>
        )}
      </Box>
    </ClinicDialog>
  );
};
